import { Synchronizer } from 'tinybase/synchronizers';
import { MergeableStore } from 'tinybase/with-schemas';

import {
  PathPersister,
  RowIdKeys,
  UserStoreSchemas,
  UserStoreTables,
} from '~/common/types/tinybase';

import { SyncStoreWrapper } from './SyncStoreWrapper';
import { newTempIdMap } from './utils';

export type UserStoreRowIdKeys = typeof userStoreRowIdKeys;

export const userStoreRowIdKeys = {
  userDirectory: 'camdenAccountName',
  messages: 'messageId',
  tempIdMaps: 'tempId',
  propertyDetails: 'propertyId',
  pcfraPropertyDetails: 'propertyId',
  tenancies: 'tenancyId',
  people: 'personId',
  pcfraPeople: 'personId',
  peopleRiskFactors: 'itemId',
  peopleAttributes: 'attributeId',
  contactDetails: 'detailsId',
  cases: 'caseId',
  serviceRequests: 'serviceRequestId',
  worksOrders: 'worksOrderId',
  caseEvents: 'eventId',
  caseSupportingOfficers: 'supportingOfficerId',
  visits: 'visitId',
  notes: 'noteId',
  fireSafetyNotes: 'noteId',
  housingApplications: 'applicationId',
  applicationApplicants: 'applicantId',
  applicationAllocations: 'allocationId',
  arrearsArrangements: 'arrangementId',
  pcfraReferrers: 'personId',
  pcfraDrafts: 'draftId',
  pcfraDocuments: 'documentId',
} satisfies RowIdKeys<UserStoreTables>;

export class BaseUserStore extends SyncStoreWrapper<
  UserStoreTables,
  UserStoreSchemas,
  UserStoreRowIdKeys
> {
  public constructor(
    public readonly userId: string,
    store: MergeableStore<UserStoreSchemas>,
    persister?: PathPersister,
    synchronizer?: Synchronizer,
  ) {
    super(store, userStoreRowIdKeys, persister, synchronizer);
  }

  /** Enables server-side listeners if previously deactivated (UserStore.ts) */
  public setActive() {
    this.store.setValue('isActive', true);
  }

  /** Disables server-side listeners and purges the store tables (UserStore.ts) */
  public setInactive() {
    this.store.setValue('isActive', false);
  }

  public addTempIdMap(tempId: ScalarId, parentTempId?: ScalarId) {
    this.put('tempIdMaps', newTempIdMap(String(tempId), String(parentTempId)));
  }

  public addRealIdToMaps(tempId: ScalarId, realId: ScalarId) {
    this.store.transaction(() => {
      const strTempId = String(tempId);
      const strRealId = String(realId);

      this.patch('tempIdMaps', { tempId: strTempId, realId: strRealId });

      const tempIdMaps = this.store.getTable('tempIdMaps');
      const tempIdMapChildrenRowIds = Object.keys(tempIdMaps).filter(
        (rowId) => tempIdMaps[rowId].parentTempId === strTempId,
      );

      tempIdMapChildrenRowIds.forEach((childTempId) =>
        this.patch('tempIdMaps', { tempId: childTempId, parentRealId: strRealId }),
      );
    });
  }
}
